import { audioGraph, midiToFreq, sfxEnabled, type AudioGraph } from "./engine.ts";

/**
 * A tiny made-up voice for the companion: a few soft "syllables" of a
 * filtered triangle with a pitch contour per mood, like a toy murmuring.
 * Quiet on purpose; it follows the sound setting.
 */
export type VoiceMood = "bright" | "soft" | "curious" | "sleepy";

/** Semitone steps per syllable, and syllable length in seconds. */
const CONTOURS: Record<VoiceMood, { steps: readonly number[]; length: number }> = {
  bright: { steps: [0, 4, 7], length: 0.085 },
  soft: { steps: [4, 2, 0, -3], length: 0.12 },
  curious: { steps: [0, 3, 8], length: 0.09 },
  sleepy: { steps: [-3, -7], length: 0.2 },
};

function syllable(graph: AudioGraph, midi: number, t: number, length: number): void {
  const { ctx } = graph;
  const freq = midiToFreq(midi);
  const osc = ctx.createOscillator();
  const formant = ctx.createBiquadFilter();
  const env = ctx.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(freq, t);
  osc.frequency.linearRampToValueAtTime(freq * 1.04, t + length * 0.6);
  osc.frequency.linearRampToValueAtTime(freq * 0.98, t + length);
  formant.type = "bandpass";
  formant.Q.value = 1.6;
  formant.frequency.setValueAtTime(freq * 2.6, t);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(0.07, t + 0.014);
  env.gain.exponentialRampToValueAtTime(0.0001, t + length);
  osc.connect(formant).connect(env).connect(graph.sfx);
  osc.start(t);
  osc.stop(t + length + 0.05);
}

/** `pitch` shifts the whole voice (e.g. a little higher for some characters). */
export function playVoice(mood: VoiceMood, pitch = 0): void {
  const graph = audioGraph();
  if (!graph || !sfxEnabled() || graph.ctx.state !== "running") return;
  const { steps, length } = CONTOURS[mood];
  const start = graph.ctx.currentTime + 0.01;
  steps.forEach((step, index) => {
    const wobble = (index % 2 === 0 ? 1 : -1) * 0.3;
    syllable(graph, 76 + pitch + step + wobble, start + index * (length + 0.025), length);
  });
}
